import { lens } from "@dhmk/zustand-lens";
import { generateId } from "ai";
import { getImaginePreviewToken } from "@/lib/utils/get-imagine-preview-token";
import { imagineStore } from "../imagine.store";

type PreviewStateState = {
  randomGenerationKey: string; // Used to trigger an iframe refresh
  previewUrl: URL | null;
  previewToken: string | null;
  previewUrlWithPreviewToken: URL | null;
  isElementSelectorEnabled: boolean;
  viewMode: "desktop" | "mobile";
};

type PreviewStateActions = {
  setPreviewUrl: (previewUrl: URL) => Promise<void>;
  setPreviewToken: (token: string) => void;
  setElementSelectorEnabled: (enabled: boolean) => void;
  refreshIframe: () => void;
  setViewMode: (viewMode: "desktop" | "mobile") => void;
  reset: () => void;
};

export type PreviewStateSlice = PreviewStateState & PreviewStateActions;

const defaultPreviewState: PreviewStateState = {
  randomGenerationKey: generateId(),
  previewUrl: null,
  previewToken: null,
  previewUrlWithPreviewToken: null,
  isElementSelectorEnabled: false,
  viewMode: "desktop",
};

function buildTokenizedUrl(cleanUrl: URL, previewToken: string) {
  // Replace host with proxy host
  const splitHost = cleanUrl.host.split(".");
  const subDomain = splitHost[0];
  const restHost = splitHost.slice(1).join(".");
  const proxyUrlWithToken = `${cleanUrl.protocol}//${subDomain}-preview.${restHost}${cleanUrl.pathname}?imaginePreviewToken=${previewToken}`;
  return new URL(proxyUrlWithToken);
}

export const previewStateSlice = lens<PreviewStateSlice>((set, get, api) => ({
  ...defaultPreviewState,
  setViewMode: (viewMode: "desktop" | "mobile") => {
    set({ viewMode });
  },
  setPreviewUrl: async (previewUrl: URL) => {
    const projectId = imagineStore.getState().projectId;
    const isReadonly = imagineStore.getState().isReadonly;

    if (isReadonly) {
      set({
        previewUrl,
        previewToken: null,
        previewUrlWithPreviewToken: null,
      });
      return;
    }

    // Set the preview URL immediately
    set({ previewUrl });

    // Fetch the token
    let previewToken: string;
    try {
      previewToken = await getImaginePreviewToken({
        projectId: projectId!,
      });

      if (
        !previewToken ||
        typeof previewToken !== "string" ||
        previewToken.length === 0
      ) {
        return;
      }
    } catch (error) {
      console.error("Could not fetch preview token, setting preview URL without a token");


      set({
        previewUrl,
        previewToken: "NO_TOKEN",
        previewUrlWithPreviewToken: previewUrl,
      });
      
      return;
    }

    // Build the tokenized URL using the CAPTURED previewUrl parameter (not state)
    const previewUrlWithPreviewToken = buildTokenizedUrl(
      previewUrl,
      previewToken,
    );
    previewUrlWithPreviewToken.searchParams.set(
      "imaginePreviewToken",
      previewToken,
    );

    // Final atomic update - set everything to ensure consistency
    set({
      previewUrl,
      previewUrlWithPreviewToken,
      previewToken,
    });
  },
  setPreviewToken: (previewToken: string) => {
    const previewUrl = get().previewUrl;

    if (!previewUrl) {
      set({ previewUrlWithPreviewToken: null, previewToken: null });
      return;
    }

    const previewUrlWithPreviewToken = buildTokenizedUrl(
      previewUrl,
      previewToken,
    );

    previewUrlWithPreviewToken.searchParams.set(
      "imaginePreviewToken",
      previewToken,
    );
    set({ previewUrlWithPreviewToken, previewToken });
  },
  setElementSelectorEnabled: (enabled: boolean) => {
    set({ isElementSelectorEnabled: enabled });
  },
  refreshIframe: () => {
    set({ randomGenerationKey: generateId() });
  },
  reset: () => {
    set({
      ...defaultPreviewState,
    });
  },
}));
